import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { SidebarInset, SidebarProvider } from "@workspace/ui/components/sidebar";
import { SearchProvider } from "@/components/command/context/search-provider";
import { Search } from "@/components/command/search";
import { ProfileDropdown } from "@/components/profile-dropdown";
import { getSession, signOut } from "@/features/auth/auth-client";
import { Header } from "@/layout/header";
import { AppSidebar } from "@/layout/sidebar";
import { getCookie } from "@/lib/cookies";
import { cn } from "@/lib/utils";
import { SettingDrawer } from "@/settings";
import { LayoutProvider } from "@/settings/context/layout-provider";
import { ThemeSwitch } from "@/theme/theme-switch";

export const Route = createFileRoute("/_authenticated")({
  component: AuthenticatedLayout,
  beforeLoad: async ({ location }) => {
    const { data } = await getSession();
    if (!data?.session) {
      throw redirect({ to: "/sign-in", search: { redirect: location.href }, replace: true });
    }

    const roles = data.user.role?.split(",").map((role) => role.trim()) ?? [];
    const dashboardRole: "admin" | "editor" | undefined = roles.includes("admin")
      ? "admin"
      : roles.includes("editor")
        ? "editor"
        : undefined;

    if (!dashboardRole) {
      await signOut();
      throw redirect({ to: "/sign-in", search: { redirect: location.href }, replace: true });
    }

    return { dashboardUser: data.user, dashboardRole };
  },
});

function AuthenticatedLayout() {
  const { dashboardUser, dashboardRole } = Route.useRouteContext();
  const defaultOpen = getCookie("sidebar_state") !== "false";
  return (
    <SearchProvider>
      <LayoutProvider>
        <SidebarProvider defaultOpen={defaultOpen}>
          <AppSidebar user={dashboardUser} role={dashboardRole} />
          <SidebarInset
            className={cn(
              // Set content container, so we can use container queries
              "@container/content",

              // If layout is fixed, set the height
              // to 100svh to prevent overflow
              "has-data-[layout=fixed]:h-svh",

              // If layout is fixed and sidebar is inset,
              // set the height to 100svh - spacing (total margins) to prevent overflow
              "peer-data-[variant=inset]:has-data-[layout=fixed]:h-[calc(100svh-(var(--spacing)*4))]",
            )}
          >
            <Header fixed className="border-b">
              <Search />
              <div className="ms-auto flex items-center space-x-4">
                <ThemeSwitch />
                <SettingDrawer />
                <ProfileDropdown user={dashboardUser} role={dashboardRole} />
              </div>
            </Header>
            <Outlet />
          </SidebarInset>
        </SidebarProvider>
      </LayoutProvider>
    </SearchProvider>
  );
}
